import classes from "./PrivacyPage.module.css";
import { Helmet, HelmetProvider } from "react-helmet-async";

const PrivacyPage = () => <div className={classes.background}>
  <HelmetProvider><Helmet>
    <title>Privacy Notice | Guitar Charts</title>
    <meta name="description" content="How Guitar Charts uses account details, private saved charts, browser storage and optional analytics." />
  </Helmet></HelmetProvider>
  <div className={classes["a4-paper"]}><div className={classes.content}>
    <h1>Privacy Notice</h1>
    <p>Updated 25 October 2026.</p>
    <p>Guitar Charts is operated by Steve Gilson. For privacy questions, requests
      about your data or account deletion, email <a href="mailto:jambole0192@gmail.com">jambole0192@gmail.com</a>.</p>

    <h2>Using charts and accounts</h2>
    <p>You can create, preview, print and download charts without an account.
      Accounts are invitation-only. If you request access or a password reset,
      the operator handles your email and personally sends the appropriate link.</p>
    <p>Supabase Auth handles your email address, password and sign-in sessions.
      Passwords are not stored in chart records. When you choose Save chart set,
      we store the chart title, introduction, musical settings, edited diagrams,
      chosen Library location and creation/update information.</p>
    <p>Private saved charts are available to your account through the app. The site
      operator and service providers may access stored data where needed to run,
      support or secure the service. Browsing a public Library chart does not
      automatically save it to your account.</p>
    <p>We use account and saved-chart information to provide the service you
      request. We use limited technical records to operate and protect the site,
      based on our legitimate interests in keeping the service reliable and secure.</p>

    <h2>Shared links and downloads</h2>
    <p>A published chart link contains the chart itself, including its title and
      introduction. Anyone who receives that link can view or copy its contents
      without signing in. Saving a chart privately does not make a link you have
      already shared private. Avoid including confidential information in shared charts.</p>
    <p>PDF and image downloads are generated in your browser. Downloading does not
      upload a new saved chart. Copies you download or send to someone else are
      outside the site's account-deletion controls.</p>

    <h2>Browser storage</h2>
    <p>Sign-in session information is stored in your browser so you can remain
      signed in and restore your session. Sign out when using a shared device.
      Signing out retains the current editor draft in that open page.</p>
    <p>Unsaved charts and practice settings are held in memory and normally reset
      when you reload or close the page. Supported account preferences sync to
      your account when you sign in.
      Your analytics choice is stored separately in this browser so we can remember it.</p>

    <h2>Optional usage analytics</h2>
    <p>Google Analytics is loaded only after you choose Allow usage analytics.
      Declining does not affect chart editing, downloads or account saving.
      Use Privacy choices on public pages to change your decision at any time.</p>
    <p>Analytics receives general site-section labels and a general ear-training
      interaction count, together with technical request information such as IP
      address and browser information. It does not receive chart titles or content,
      shared-chart query strings, account identifiers, or invitation/reset links.
      Account pages and private chart pages are excluded.</p>
    <p>This implementation uses cookieless analytics measurements and does not
      enable advertising storage or Google advertising signals. Withdrawing your
      choice stops further analytics loading; it does not undo earlier processing
      for which you gave permission. See <a href="https://policies.google.com/privacy">Google's privacy policy</a>.</p>

    <h2>Service providers and technical records</h2>
    <p>We use <a href="https://supabase.com/privacy">Supabase</a> for authentication
      and saved data, and <a href="https://www.netlify.com/privacy/">Netlify</a>
      for website hosting and delivery. They process technical records needed to
      deliver and protect their services, which can include IP addresses, request
      URLs, times and browser information. Account links are one-time access links;
      keep them confidential.</p>
    <p>Access requests and support messages sent to the contact address are handled
      through the operator's email service. Support buttons and images can contact
      Buy Me a Coffee; following external links is subject to those providers'
      own privacy information.</p>
    <p>These providers operate internationally and may process information outside
      the UK. Provider contracts, hosting settings and applicable transfer arrangements
      govern that processing. Contact the operator for details of the current
      hosting region and transfer safeguards.</p>

    <h2>Retention and deletion</h2>
    <p>Saved charts remain while your account is active unless you delete them.
      From the Account page you can download a JSON export of your saved charts
      and synced preferences, or permanently delete your account after confirming
      your email address. Removing an account deletes its active saved charts
      and preferences. Provider logs, backups, correspondence and
      copies already downloaded or shared are separate and may have different
      retention periods.</p>
    <p>Support correspondence is kept for handling your request and necessary
      service administration. Technical-record and backup retention depends on
      provider settings; contact the operator for details. We do not promise that
      all provider-held copies disappear immediately when an active account is deleted.</p>

    <h2>Your choices and rights</h2>
    <p>Use the Account page to export your saved charts and synced preferences or
      delete your account. For other access, correction, restriction or portability
      requests, or to object to processing based on legitimate interests, contact
      the operator. The rights that apply depend on the circumstances.</p>
    <p>You can withdraw permission for optional analytics through Privacy choices.
      You can also complain to the <a href="https://ico.org.uk/make-a-complaint/">UK Information Commissioner's Office</a>.</p>
    <p>We will update this notice when the way the service handles personal data changes.</p>
  </div></div>
</div>;

export default PrivacyPage;
